/** Pixels of slack before a moving layer snaps to a guide. */
export const ALIGN_SNAP_PX = 6;

function axisAlignedFrame(frame) {
  const x = Number(frame.x) || 0;
  const y = Number(frame.y) || 0;
  const width = Number(frame.width) || 0;
  const height = Number(frame.height) || 0;
  const rotation = Number(frame.rotation) || 0;
  if (!rotation || !width || !height) {
    return { x, y, width, height };
  }
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const rw = width * cos + height * sin;
  const rh = width * sin + height * cos;
  const cx = x + width / 2;
  const cy = y + height / 2;
  return { x: cx - rw / 2, y: cy - rh / 2, width: rw, height: rh };
}

function edgesOf(frame) {
  return {
    left: frame.x,
    right: frame.x + frame.width,
    cx: frame.x + frame.width / 2,
    top: frame.y,
    bottom: frame.y + frame.height,
    cy: frame.y + frame.height / 2,
  };
}

/**
 * Snap a moving layer to the canvas (center + edges) and to other layers
 * (edges + centers). The returned x/y keep the same delta as the snapped box.
 */
export function snapFrameToAlignGuides(
  frame,
  others = [],
  canvasW = 0,
  canvasH = 0,
  threshold = ALIGN_SNAP_PX
) {
  const empty = {
    x: frame.x,
    y: frame.y,
    guides: { vertical: [], horizontal: [] },
  };
  const box = axisAlignedFrame(frame);
  if (!canvasW || !canvasH || !box.width || !box.height) {
    return empty;
  }

  const drag = edgesOf(box);
  const targets = [
    {
      left: 0,
      right: canvasW,
      cx: canvasW / 2,
      top: 0,
      bottom: canvasH,
      cy: canvasH / 2,
    },
    ...others.map((other) => edgesOf(axisAlignedFrame(other))),
  ];

  let bestDx = threshold + 1;
  let bestDy = threshold + 1;
  let shiftX = 0;
  let shiftY = 0;
  let guideV = null;
  let guideH = null;

  for (const target of targets) {
    const xCandidates = [
      { dragEdge: drag.left, edge: target.left, guide: target.left },
      { dragEdge: drag.right, edge: target.right, guide: target.right },
      { dragEdge: drag.cx, edge: target.cx, guide: target.cx },
    ];
    for (const candidate of xCandidates) {
      const distance = Math.abs(candidate.dragEdge - candidate.edge);
      if (distance <= threshold && distance < bestDx) {
        bestDx = distance;
        shiftX = candidate.edge - candidate.dragEdge;
        guideV = candidate.guide;
      }
    }

    const yCandidates = [
      { dragEdge: drag.top, edge: target.top, guide: target.top },
      { dragEdge: drag.bottom, edge: target.bottom, guide: target.bottom },
      { dragEdge: drag.cy, edge: target.cy, guide: target.cy },
    ];
    for (const candidate of yCandidates) {
      const distance = Math.abs(candidate.dragEdge - candidate.edge);
      if (distance <= threshold && distance < bestDy) {
        bestDy = distance;
        shiftY = candidate.edge - candidate.dragEdge;
        guideH = candidate.guide;
      }
    }
  }

  return {
    x: frame.x + (guideV != null ? shiftX : 0),
    y: frame.y + (guideH != null ? shiftY : 0),
    guides: {
      vertical: guideV != null ? [guideV] : [],
      horizontal: guideH != null ? [guideH] : [],
    },
  };
}
